import { useTranslation } from '../../../../lib/i18n';
import { messageDir } from '../../../../lib/message-direction';
import { TASK_DETAILS_PAGE } from '../../../../lib/tasks-bi';
import { Markdown } from '../../../Markdown';
import { Modal } from '../../../Modal';
import { Button, OverflowMenu, StatusBadge } from '../../ui';
import { AgentConversationCard } from '../AgentConversationCard';
import { TaskActivityTimeline } from '../TaskActivityTimeline';
import { TaskFilesSection } from '../TaskFilesSection';
import { TaskSentOutreachSection } from '../TaskSentOutreachSection';
import { TaskChatOriginLine, TaskOriginLine } from '../TaskOriginLine';
import { TaskDecisionPrompts } from '../TaskDecisionPrompts';
import { TaskUserEdits } from '../TaskUserEdits';
import { useTaskDetailModal } from '../use-task-detail-modal';
import { CancelTaskConfirm } from '../CancelTaskConfirm';
import taskStyles from '../TaskDetailsPanel.module.css';
import styles from '../TaskDetailModal.module.css';

// The wide task-details drill-in: the DS centered <Modal>. The task title is the modal header; the body
// pairs the description with the status badge and shows the agent-activity timeline; the footer carries
// Close and "Resolve with {agent}". Behaviour lives in useTaskDetailModal — this file is only its wide look.
export function TaskDetailDesktop({ taskId }: { taskId: string }) {
  const vm = useTaskDetailModal(taskId);
  const { t } = useTranslation();

  if (vm.kind === 'loading') {
    return (
      <Modal open onClose={vm.close} size="medium" backAction={vm.back}>
        <div className={taskStyles.skel}>
          <div className="orion-shimmer" style={{ height: 18, width: '60%' }} />
          <div className="orion-shimmer" style={{ height: 13, width: '90%' }} />
          <div className="orion-shimmer" style={{ height: 13, width: '80%' }} />
        </div>
      </Modal>
    );
  }
  if (vm.kind === 'error') {
    return (
      <Modal open onClose={vm.close} size="medium" title={t('tasks.detail.title')} labelledBy="task-detail-title" headerSeparator backAction={vm.back}>
        <div className={taskStyles.error}>{t('tasks.detail.notFound')}</div>
      </Modal>
    );
  }

  const { task, title, description } = vm;
  return (
    <Modal
      open
      onClose={vm.close}
      size="medium"
      maxHeightPx={650}
      title={title}
      labelledBy="task-detail-title"
      headerSeparator
      backAction={vm.back}
      footerSeparator
      scrollBody
      footer={
        <>
          {/* The secondary actions — the displaced Maestro handoff, and Cancel Task while the task is
              still open — live in a menu rather than as buttons. Cancel is destructive and not undoable,
              so it must not sit at button weight beside the action the reader came to take. Pushed to the
              footer's inline-start so it reads as its own group opposite the two buttons. */}
          {vm.menu.length > 0 && (
            <div className={styles.footerMenu}>
              <OverflowMenu
                ariaLabel={t('tasks.rowMenu.label', { title })}
                actions={vm.menu.map((item) => ({
                  key: item.key,
                  label: item.label,
                  destructive: item.destructive,
                  onSelect: item.onClick,
                }))}
              />
            </div>
          )}
          {/* NOT "Close": sitting beside "Resolve with {agent}" that read as closing the TASK. This
              button only dismisses the dialog, and now says so. */}
          <Button variant="outlined" onClick={vm.close}>
            {t('tasks.detail.closeDetails')}
          </Button>
          <Button onClick={vm.cta.onClick}>{vm.cta.label}</Button>
        </>
      }
    >
      {/* The task status + plan, then the produced files, then the agent's own conversation tucked into a
          collapsible card so the modal opens on the plan and reveals the transcript on demand. */}
      <div className={styles.body}>
        {/* Where this task came from: the goal that laid it out when there is one, otherwise the chat it
            was born in — the origin every task has (`taskChatOrigin`). Never both. */}
        {vm.goalOrigin && <TaskOriginLine origin={vm.goalOrigin} />}
        {vm.chatOrigin && <TaskChatOriginLine onOpen={vm.onOpenChatOrigin} />}
        <div className={styles.descRow}>
          {description && (
            <div className={styles.desc}>
              <Markdown variant="compact" dir={messageDir(description)}>
                {description}
              </Markdown>
            </div>
          )}
          <StatusBadge status={task.status} detail notStarted={vm.notStarted} awaitingDecision={vm.awaitingDecision} />
        </div>
        {/* The question this task is held on, answered in place — the drill-in's half of the row's own
            chips. `TASK_DETAILS_PAGE` so a tap here is not misattributed to the Tasks list. */}
        <TaskDecisionPrompts task={task} origin={vm.goalOrigin} onAnsweredChange={vm.onDecisionAnswered} page={TASK_DETAILS_PAGE} />
        <TaskActivityTimeline
          agentId={task.agentId}
          agentName={vm.agentName}
          steps={task.steps}
          currentStep={vm.currentStep}
          statusVariant={vm.statusVariant}
          isLive={vm.isLive}
        />
        <TaskSentOutreachSection outreaches={vm.sentOutreaches} />
        <TaskFilesSection files={vm.files} onOpen={vm.onOpenFile} />
        {/* The two writes the drill-in was missing: restate the task, and note on it. One shared
            component with the phone sheet, so the two layouts cannot grow two different forms. */}
        <TaskUserEdits task={task} editable={vm.editable} edits={vm.edits} notes={vm.notes ?? []} />
        <AgentConversationCard taskId={taskId} dashboardBuild={!!task.isDashboardBuild} />
        <CancelTaskConfirm flow={vm.cancel} />
      </div>
    </Modal>
  );
}
